import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { CopyText } from "src/components/features/small/CopyText/CopyText";
import { EXPIRY_OPTIONS, type TokenType } from "../../GenerateTokenForm.hooks";

const TOKEN_PURPOSES: {
  type: TokenType;
  icon: typeof HubOutlinedIcon;
  header: string;
  title: string;
  description: string;
}[] = [
  {
    type: "api",
    icon: TerminalOutlinedIcon,
    header: "API TOKEN",
    title: "Use the REST API",
    description: "Automation, CI/CD pipelines, scripts and programmatic access.",
  },
  {
    type: "agent",
    icon: HubOutlinedIcon,
    header: "AGENT TOKEN",
    title: "Connect a data platform",
    description:
      "Spark, DBT, Databricks or Airflow reporting metrics & metadata to Definity.",
  },
];

export interface GenerateTokenFormContentProps {
  generatedToken: string | null;
  typeLabel: string;
  tokenType: TokenType;
  onTokenTypeChange: (value: TokenType) => void;
  label: string;
  onLabelChange: (value: string) => void;
  expiresInDays: number;
  onExpiresInDaysChange: (value: number) => void;
  readOnly: boolean;
  onReadOnlyChange: (value: boolean) => void;
}

export default function GenerateTokenFormContent({
  generatedToken,
  typeLabel,
  tokenType,
  onTokenTypeChange,
  label,
  onLabelChange,
  expiresInDays,
  onExpiresInDaysChange,
  readOnly,
  onReadOnlyChange,
}: GenerateTokenFormContentProps) {
  if (generatedToken) {
    return (
      <>
        <p className="text-sm text-text-secondary">
          Your {typeLabel} token is ready. Copy it now: it can't be shown again.
        </p>
        <TextField
          value={generatedToken}
          fullWidth
          size="small"
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <CopyText
                    text={generatedToken}
                    buttonOnly
                    phContext={`${tokenType}_token`}
                  />
                </InputAdornment>
              ),
            },
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="text-sm font-medium">What is this token for?</div>
        <div className="flex gap-3">
          {TOKEN_PURPOSES.map(({ type, icon: Icon, header, title, description }) => {
            const selected = tokenType === type;
            return (
              <Card
                key={type}
                variant="outlined"
                className={`flex-1 ${
                  selected ? "border-primary bg-action-hover" : "border-divider"
                }`}
              >
                <CardActionArea
                  data-test-id={`generate-token-purpose-${type}`}
                  onClick={() => onTokenTypeChange(type)}
                  aria-pressed={selected}
                  className="h-full flex flex-col items-start gap-1 p-3"
                >
                  <div className="flex w-full items-center justify-between">
                    <Icon fontSize="small" color={selected ? "primary" : "action"} />
                    {selected && (
                      <CheckCircleIcon fontSize="small" color="primary" />
                    )}
                  </div>
                  <div className="text-sm font-bold uppercase tracking-wide text-text-secondary">
                    {header}
                  </div>
                  <div className="text-sm font-medium">{title}</div>
                  <p className="m-0 text-xs text-text-secondary">{description}</p>
                </CardActionArea>
              </Card>
            );
          })}
        </div>
      </div>
      <TextField
        label="Label (optional)"
        placeholder={
          tokenType === "agent"
            ? "e.g. Spark production cluster"
            : "e.g. Production CI/CD"
        }
        value={label}
        onChange={(e) => onLabelChange(e.target.value)}
        fullWidth
        size="small"
      />
      <TextField
        label="Expires in"
        select
        value={expiresInDays}
        onChange={(e) => onExpiresInDaysChange(Number(e.target.value))}
        fullWidth
        size="small"
      >
        {EXPIRY_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
      {tokenType === "api" && (
        <div className="flex flex-col gap-1">
          <FormControlLabel
            control={
              <Checkbox
                checked={readOnly}
                onChange={(e) => onReadOnlyChange(e.target.checked)}
                size="small"
              />
            }
            label="Read-only"
          />
          <p className="text-xs text-text-secondary m-0">
            Read-only tokens can call read (GET) endpoints only. Any write request —
            create, update, delete, or reprocess — is rejected.
          </p>
        </div>
      )}
    </>
  );
}
